import { useEffect, useMemo } from 'react';
import { createBrowserRouter, RouterProvider, Routes, Route } from 'react-router-dom';
import { startOutboxWatchdog } from './offline/sync';
import { requestPersistentStorage } from './offline/persistentStorage';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { ErrorBoundary } from './components/ErrorBoundary';
import { RouteErrorPage, ViewErrorBoundary } from './components/ViewErrorBoundary';
import { SessionActivityTracker } from '@/components/SessionActivityTracker';
import { SessionExpiredDialog } from '@/components/SessionExpiredDialog';
import { SessionExpiryWarning } from '@/components/SessionExpiryWarning';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Login } from './pages/Login';
import { AuthCallback } from './pages/AuthCallback';
import { LoggedOut } from './pages/LoggedOut';
import { LandingPageRedirect } from './components/LandingPageRedirect';
import { Layout } from './components/Layout';
import { appRoutes } from './routes/index.custom';
import { createAppTheme } from './theme';
import { cachedThemeColors } from './lib/themeCache';
import { CACHE_TIME } from './config/queryDefaults';
import { UserPreferencesProvider } from './contexts/UserPreferencesContext';
import { ThemePreferenceProvider } from './contexts/ThemeContext';
import { HomeDashboardProvider } from './contexts/HomeDashboardContext';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: CACHE_TIME.DEFAULT,
    },
  },
});

// Expose queryClient in dev (console access) and in the e2e build (VITE_E2E=true): the Playwright
// suite runs against a production build in CI and waits on react-query being idle
// (e2e/generated/planEntryGridHelpers.ts). A real production build never sets VITE_E2E.
if (import.meta.env.DEV || import.meta.env.VITE_E2E === 'true') {
  (window as any).__queryClient = queryClient;
}

// Dev: Ctrl+Shift+Q clears all caches
if (import.meta.env.DEV) {
  document.addEventListener('keydown', (e) => {
    if (e.ctrlKey && e.shiftKey && e.key === 'Q') {
      queryClient.clear();
      console.log('[DEV] All query caches cleared');
    }
  });
}

function AuthenticatedApp() {
  useEffect(() => {
    startOutboxWatchdog();
    void requestPersistentStorage();
  }, []);

  return (
    <UserPreferencesProvider>
      <ThemePreferenceProvider>
        <HomeDashboardProvider>
          <Layout>
            <ViewErrorBoundary>
              <Routes>
                {appRoutes.map((route, index) => (
                  <Route key={index} path={route.path} element={route.element} />
                ))}
                <Route path="/" element={<LandingPageRedirect />} />
              </Routes>
            </ViewErrorBoundary>
          </Layout>
        </HomeDashboardProvider>
      </ThemePreferenceProvider>
    </UserPreferencesProvider>
  );
}

/**
 * A DATA router (createBrowserRouter + RouterProvider), not <BrowserRouter>.
 *
 * This is what makes the unsaved-changes guard possible: `useBlocker` (ViewToolbar) resolves its
 * router from `useDataRouterContext`, which only a RouterProvider supplies — under <BrowserRouter>
 * it throws. Without it the confirm dialog could only ever hang off the toolbar's own back button,
 * so leaving a dirty view via the main menu or the browser's back button silently discarded the work.
 *
 * The route tree itself is unchanged: `/*` still renders a descendant <Routes> inside
 * AuthenticatedApp. Descendant routes get no data APIs (loaders/actions) — we use none — but they
 * navigate through this router, so the blocker still sees and can stop them.
 *
 * Module scope on purpose: re-creating the router on every render would reset history state.
 */
const router = createBrowserRouter([
  { path: '/login', element: <Login /> },
  // Public auth-flow routes (code+PKCE callback + post-logout landing, D13b).
  { path: '/auth/callback', element: <AuthCallback /> },
  { path: '/auth/logged-out', element: <LoggedOut /> },
  {
    path: '/*',
    element: (
      <ProtectedRoute>
        <AuthenticatedApp />
      </ProtectedRoute>
    ),
  },
].map((route) => ({ ...route, errorElement: <RouteErrorPage /> })));

function App() {
  // Use the cached colours from localStorage to avoid accent flash on reload
  const initialTheme = useMemo(() => createAppTheme('light', cachedThemeColors('light')), []);

  return (
    <ErrorBoundary>
      <ThemeProvider theme={initialTheme}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <QueryClientProvider client={queryClient}>
            {/* Both are app-wide and routerless on purpose: the session can end on any
                screen, and the activity gate must keep counting across navigation. */}
            <SessionActivityTracker />
            <SessionExpiryWarning />
            <SessionExpiredDialog />
            <RouterProvider router={router} />
          </QueryClientProvider>
        </LocalizationProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
